import { useEffect, useState } from "react";
import { formatCompletedTime } from "../../intelligence/intelligenceLabels";
import SourceHealthBadge from "../SourceHealthBadge";
import {
  DATA_SOURCE_SCHEDULE_INTERVALS,
  intervalMinutesFromSource,
  schedulePayloadFromInterval,
} from "./dataSourceScheduleUtils";

const DataSourceScheduleModal = ({
  source,
  isOpen,
  isSaving,
  onClose,
  onSave,
  intervals = DATA_SOURCE_SCHEDULE_INTERVALS,
}) => {
  const [scheduleEnabled, setScheduleEnabled] = useState(false);
  const [intervalMinutes, setIntervalMinutes] = useState("");
  const [cronExpression, setCronExpression] = useState("");

  useEffect(() => {
    if (!source || !isOpen) {
      return;
    }

    setScheduleEnabled(Boolean(source.scheduleEnabled));
    const minutes = intervalMinutesFromSource(source);
    setIntervalMinutes(minutes == null ? "" : String(minutes));
    setCronExpression(source.scheduleCronExpression || "");
  }, [source, isOpen]);

  if (!isOpen || !source) {
    return null;
  }

  const handleSubmit = (event) => {
    event.preventDefault();
    const minutes = intervalMinutes === "" ? null : Number(intervalMinutes);
    const payload = schedulePayloadFromInterval(minutes, scheduleEnabled && minutes != null);

    if (cronExpression.trim()) {
      payload.scheduleCronExpression = cronExpression.trim();
    }

    onSave(source, payload);
  };

  const displayName = source.displayName || source.sourceName;

  return (
    <div className="admin-re-modal-backdrop" role="presentation" onClick={onClose}>
      <div
        className="admin-re-modal panel-card"
        role="dialog"
        aria-labelledby="schedule-modal-title"
        onClick={(event) => event.stopPropagation()}
      >
        <header className="admin-re-modal-header">
          <div>
            <p className="eyebrow">Schedule</p>
            <h2 id="schedule-modal-title">Schedule Source: {displayName}</h2>
          </div>
          <button type="button" className="midpoint-share-button" onClick={onClose}>
            Close
          </button>
        </header>

        <form className="admin-re-modal-body" onSubmit={handleSubmit}>
          <label className="admin-re-modal-field admin-re-modal-toggle">
            <input
              type="checkbox"
              checked={scheduleEnabled}
              onChange={(event) => setScheduleEnabled(event.target.checked)}
            />
            <span>Schedule enabled</span>
          </label>

          <label className="admin-re-modal-field">
            <span>Run interval</span>
            <select
              value={intervalMinutes}
              disabled={!scheduleEnabled}
              onChange={(event) => setIntervalMinutes(event.target.value)}
            >
              {intervals.map((interval) => (
                <option
                  key={interval.label}
                  value={interval.minutes == null ? "" : String(interval.minutes)}
                >
                  {interval.label}
                </option>
              ))}
            </select>
          </label>

          <label className="admin-re-modal-field">
            <span>Cron expression (optional)</span>
            <input
              type="text"
              value={cronExpression}
              placeholder="e.g. 0 */6 * * *"
              onChange={(event) => setCronExpression(event.target.value)}
            />
          </label>

          <div className="admin-re-modal-readonly">
            <p>
              <strong>Last run:</strong> {formatCompletedTime(source.lastRunAt)}
            </p>
            <p>
              <strong>Next run:</strong>{" "}
              {source.scheduleEnabled && source.nextRunAt
                ? formatCompletedTime(source.nextRunAt)
                : "Not scheduled"}
            </p>
            <p className="admin-re-modal-status">
              <strong>Status:</strong>{" "}
              <SourceHealthBadge status={source.health || source.lastStatus || "unknown"} />
            </p>
          </div>

          <footer className="admin-re-modal-footer">
            <button type="button" className="midpoint-share-button" onClick={onClose}>
              Cancel
            </button>
            <button
              type="submit"
              className="midpoint-share-button admin-re-btn-primary"
              disabled={isSaving}
            >
              {isSaving ? "Saving…" : "Save Schedule"}
            </button>
          </footer>
        </form>
      </div>
    </div>
  );
};

export default DataSourceScheduleModal;
